import { AlertTriangle, XIcon } from "lucide-react";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import { useNewConversationFiltersStore } from "@/stores/new-conversation-filters.store";
import useWorkspace from "@/hooks/useWorkspace";
import Workspace, { WorkspaceDocument } from "@/models/workspace/Workspace";
import { useEffect, useState } from "react";
import { useConversationHub } from "@/hooks/useConversationHub";
import { flattenDocs } from "@/models/workspace/utils";
import { Tooltip, TooltipTrigger } from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import ContextTooltipContent from "./ContextTooltipContent";

interface Props {
  workspaceId: string;
  selectedIds: string[];
  inPopover?: boolean;
  isOverview?: boolean;
  dataPillItem?: boolean;
  dataWorkspacePill?: boolean;
}

const WorkspacePill: React.FC<Props> = ({
  workspaceId,
  selectedIds,
  inPopover = false,
  isOverview = false,
  dataPillItem,
  dataWorkspacePill,
}) => {
  const { workspaceContext, setWorkspaceContext } =
    useNewConversationFiltersStore();
  const { selectedConversation } = useConversationHub();
  const { useQuerySingle } = useWorkspace();

  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [workspaceDocs, setWorkspaceDocuments] = useState<WorkspaceDocument[]>(
    []
  );
  const fetchedWorkspace = useQuerySingle(workspaceId);

  const handleRemove = () => {
    const updated = { ...workspaceContext };
    delete updated[workspaceId];
    setWorkspaceContext(updated);
  };

  const handleRemoveDocument = (documentId: string) => {
    const currentContext = workspaceContext[workspaceId] || [];
    const updatedContext = currentContext.filter(
      (item) => item.documentId !== documentId
    );

    const updated = { ...workspaceContext };
    if (updatedContext.length === 0) {
      delete updated[workspaceId];
    } else {
      updated[workspaceId] = updatedContext;
    }
    setWorkspaceContext(updated);
  };

  useEffect(() => {
    if (fetchedWorkspace.isFetched && fetchedWorkspace.data) {
      const contextItem = workspaceContext[workspaceId] || [];
      setWorkspace(fetchedWorkspace.data);
      setWorkspaceDocuments(
        flattenDocs(fetchedWorkspace.data).filter((doc) =>
          contextItem.map((item) => item.documentId).includes(doc.id)
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchedWorkspace.isFetched,
    fetchedWorkspace.data,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    workspaceContext[workspaceId],
  ]);

  const hasOutdatedDocs = workspaceDocs.some((doc) => {
    const ctxEntry = (workspaceContext[workspaceId] ?? []).find(
      (x) => x.documentId === doc.id
    );
    return (
      ctxEntry &&
      typeof ctxEntry.activeRevisionNumber === "number" &&
      ctxEntry.activeRevisionNumber !== doc.activeRevision
    );
  });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className={`flex items-center space-x-1 px-2 py-1 border-primary ${
            selectedConversation || isOverview
              ? "cursor-not-allowed"
              : "cursor-pointer"
          } ${inPopover ? "w-full justify-between" : ""}`}
          {...(dataPillItem && { "data-pill-item": true })}
          {...(dataWorkspacePill && { "data-workspace-pill": true })}
        >
          <span className="truncate max-w-[150px] font-medium">
            {workspace?.name} (
            {
              selectedIds.filter((id) =>
                workspaceDocs.some((doc) => doc.id === id)
              ).length
            }
            )
          </span>
          {hasOutdatedDocs && (
            <AlertTriangle
              className="text-destructive h-3 w-3"
              aria-label="Some documents are out of date"
            />
          )}
          {!selectedConversation && !isOverview && (
            <div className="flex">
              <Button
                onClick={handleRemove}
                className="text-destructive bg-transparent size-2 cursor-pointer p-2 shadow-none hover:bg-transparent border-transparent border border-dashed hover:border-dashed hover:border hover:border-secondary-foreground hover:text-destructive rounded-md"
                aria-label="Remove all filters for this source"
              >
                <XIcon />
              </Button>
            </div>
          )}
        </Badge>
      </TooltipTrigger>
      <ContextTooltipContent
        side={inPopover ? "right" : "bottom"}
        items={workspaceDocs.map((doc) => {
          const ctxEntry = (workspaceContext[workspaceId] ?? []).find(
            (x) => x.documentId === doc.id
          );
          const version =
            ctxEntry && typeof ctxEntry.activeRevisionNumber === "number"
              ? ctxEntry.activeRevisionNumber
              : doc.activeRevision;
          const isOutdated =
            ctxEntry &&
            typeof ctxEntry.activeRevisionNumber === "number" &&
            ctxEntry.activeRevisionNumber !== doc.activeRevision;
          return {
            id: doc.id,
            label: `${doc.name || doc.id} (Version ${version})`,
            metadata: isOutdated ? (
              <AlertTriangle
                className="text-destructive h-3 w-3 shrink-0"
                aria-label="Document version differs from active revision"
              />
            ) : undefined,
            onRemove:
              !selectedConversation && !isOverview
                ? () => handleRemoveDocument(doc.id)
                : undefined,
          };
        })}
        emptyMessage="Ingen dokumenter fundet"
        canEdit={!selectedConversation && !isOverview}
      />
    </Tooltip>
  );
};

export default WorkspacePill;
